import moment from 'moment';
import { campaignTypeMap } from '../../../../core/utils/constants/constants';
import { calculateImpressions } from '../CampaignRequestorUtil';

/**
 * Extracted form initialization logic for the new hook-based approach
 * This replaces the handleInitialValues function from the original component
 */
export const handleInitialValues = (campaignOrder, form, history) => {
  const {
    name,
    description,
    startDate,
    endDate,
    type,
    renewalStatus,
    advertiser,
    budget,
    cpm,
    isRegulated,
    category,
    attributionTargets,
    geoTargets,
    measureWalkInTraffic,
    advertiserType,
    transactionTarget,
    transactionsConfig,
    signUpConfig,
    awarenessCampaign,
    sessionConfig,
    notes,
    campaigns,
    freeImpressions,
    IONumber,
    productCode,
  } = campaignOrder;

  // Process line item data for a single line item
  const processLineItem = (lineItem, orderBudget) => {
    // Enhanced date handling with validation - use date portion only (ignore time to avoid timezone shifts)
    const processedStartDate = lineItem.startDate
      ? (moment.isMoment(lineItem.startDate) ? lineItem.startDate : moment.utc(lineItem.startDate).startOf('day'))
      : null;
    const processedEndDate = lineItem.endDate
      ? (moment.isMoment(lineItem.endDate) ? lineItem.endDate : moment.utc(lineItem.endDate).startOf('day'))
      : null;

    // Safe budget calculation
    const safeBudgetTotal = Number(lineItem.budgetTotal) || 0;
    const safeCpm = Number(lineItem.cpm) || 7;
    const safeBudget = Number(orderBudget) || 0;

    // Calculate impressions
    const impressions = calculateImpressions({
      budget: safeBudgetTotal,
      cpm: safeCpm,
    });

    // Calculate budget percentage
    const budgetPercentage = safeBudgetTotal && safeBudget
      ? Math.round((safeBudgetTotal / safeBudget) * 100)
      : 100;

    // Process targeting countries safely
    const targetingCountries = lineItem.targetingCountries && Array.isArray(lineItem.targetingCountries) && lineItem.targetingCountries.length
      ? lineItem.targetingCountries[0].code
      : null;

    // Enhanced ad group processing for cloned data
    let processedAdGroup = null;
    if (lineItem.adGroup) {
      processedAdGroup = {
        ...lineItem.adGroup,
        adGroupCreatives: (lineItem.adGroup.adGroupCreatives && Array.isArray(lineItem.adGroup.adGroupCreatives))
          ? lineItem.adGroup.adGroupCreatives.map((creative) => {
              return {
                ...creative,
                startDate: creative.startDate
                  ? (moment.isMoment(creative.startDate) ? creative.startDate : moment(creative.startDate))
                  : null,
                endDate: creative.endDate
                  ? (moment.isMoment(creative.endDate) ? creative.endDate : moment(creative.endDate))
                  : null,
                // Ensure media files are preserved
                medias: creative.medias && Array.isArray(creative.medias) ? creative.medias : [],
              };
            })
          : [],
      };
    }

    return {
      ...lineItem,
      startDate: processedStartDate,
      endDate: processedEndDate,
      impressions,
      budgetPercentage,
      targetingCountries,
      adGroup: processedAdGroup,
      // Ensure targeting data is preserved
      targetingRegions: lineItem.targetingRegions && Array.isArray(lineItem.targetingRegions) ? lineItem.targetingRegions : [],
      targetingLocations: lineItem.targetingLocations && Array.isArray(lineItem.targetingLocations) ? lineItem.targetingLocations : [],
      firstPartyDataAudiences: lineItem.firstPartyDataAudiences && Array.isArray(lineItem.firstPartyDataAudiences) ? lineItem.firstPartyDataAudiences : [],
      dealIds: lineItem.dealIds && Array.isArray(lineItem.dealIds) ? lineItem.dealIds : [],
      // Explicitly preserve radio button selections for cloning
      targetingType: lineItem.targetingType || null,
      audienceType: lineItem.audienceType || null,
    };
  };

  // Map campaigns array to form structure
  const formCampaigns = campaigns && Array.isArray(campaigns) && campaigns.length > 0
    ? campaigns.map((campaign) => ({
        id: campaign.id,
        name: campaign.name || '',
        type: campaignTypeMap[campaign.type] || campaignTypeMap[type] || 1, // Use campaign-specific type, fallback to order type
        budget: Number(campaign.budgetTotal) || 0,
        // Parse UTC dates and use date portion only (ignore time to avoid timezone shifts)
        startDate: campaign.startDate ? moment.utc(campaign.startDate).startOf('day') : null,
        endDate: campaign.endDate ? moment.utc(campaign.endDate).startOf('day') : null,
        cpm: Number(campaign.cpm) || 7,
        lineItems: campaign.lineItems && Array.isArray(campaign.lineItems)
          ? campaign.lineItems.map(lineItem => processLineItem(lineItem, budget))
          : [],
      }))
    : [
        // Default campaign structure
        {
          name: '',
          type: campaignTypeMap[type] || 1,
          budget: budget || 0,
          startDate: null,
          endDate: null,
          lineItems: [
            {
              name: 'Line Item 1',
              startDate: moment(),
              endDate: moment().add(2, 'days'),
              budgetTotal: budget || 0,
              budgetPercentage: 100,
              cpm: 7,
              regionGroups: [],
              targetingGroups: [],
              targetingType: null,
              targetingRegions: [],
              targetingLocations: [],
              targetingCountries: null,
              audienceType: null,
              firstPartyDataAudiences: [],
              adGroup: null,
              valueAdd: false,
            },
          ],
        },
      ];

  const values = {
    name: name || '',
    description,
    // Parse UTC dates and use date portion only (ignore time to avoid timezone shifts)
    startDate: startDate ? moment.utc(startDate).startOf('day') : moment(),
    endDate: endDate ? moment.utc(endDate).startOf('day') : moment().add(2, 'days'),
    type: campaignTypeMap[type],
    renewalStatus: renewalStatus || 'EXPECTED_TO_RENEW',
    advertiserId: advertiser ? advertiser.id : null,
    budget: budget || 0,
    cpm: cpm || 7,
    freeImpressions: freeImpressions || 0,
    isRegulated,
    category,
    attributionTargets: attributionTargets || [],
    geoTargets: geoTargets || [],
    measureWalkInTraffic,
    advertiserType: advertiserType || 'LOCAL_DELIVERY_SERVICE',
    transactionTarget,
    transactionsConfig: transactionsConfig || null,
    signUpConfig: signUpConfig || null,
    awarenessCampaign,
    sessionConfig: sessionConfig || null,
    notes: notes || null,
    IONumber: IONumber || '',
    productCode:
      productCode ||
      (() => {
        // Set productCode based on campaign type if not already set
        const campaignType = campaignTypeMap[type];
        if (campaignType === 7) return 'EXT'; // External
        if (campaignType === 8) return 'CTV'; // Video
        return 'DIS'; // Display (default)
      })(),
    campaigns: formCampaigns,
    impressions: calculateImpressions({ budget, cpm }),
  };

  form.setFieldsValue(values);

  // Force form re-render to ensure UI updates - critical for cloning
  setTimeout(() => {
    // Trigger a form validation to force re-render
    form.validateFields().catch(() => {
      // Ignore validation errors, we just want to trigger re-render
    });

    // Double-check that campaigns were set
    const currentFormValues = form.getFieldsValue();
  }, 100);

  // Only navigate if we have a real campaign ID (not during cloning)
  if (campaignOrder.id && history) {
    history.push(`/campaigns/requester/edit/${campaignOrder.id}`);
  } else if (!campaignOrder.id) {
    // For cloning operations, ensure the form values stick by triggering an additional refresh
    setTimeout(() => {
      form.setFieldsValue(values);
      form.validateFields().catch(() => {});
    }, 200);
  }

  return values;
};